/**
 * Presentación del producto: cuatro escenas (plato, cocina, salón y caja) que
 * rotan con fundido cruzado. La usan el login de escritorio y el hero de la
 * landing.
 *
 * OJO: esta carpeta existe dos veces, idéntica, en
 * frontend/src/components/showcase/ y landing-page/src/showcase/ (son dos
 * builds distintos). Si cambias una, copia el cambio a la otra. Los colores
 * salen de las variables del tema de la app con un respaldo claro, que es el
 * que ve la landing.
 */
import React, { useState } from 'react';
import styles from './Showcase.module.css';
import { FloorScene, KdsScene, ReceiptScene, VaporScene } from './scenes';

const SCENES = [
  { key: 'vapor', label: 'Plato', tone: 'dark' },
  { key: 'comanda', label: 'Cocina', tone: 'dark' },
  { key: 'mesas', label: 'Salón', tone: 'light' },
  { key: 'ticket', label: 'Caja', tone: 'light' },
] as const;

interface ShowcaseProps {
  /** Tiempo en pantalla de cada escena. */
  durationMs?: number;
  className?: string;
}

const Showcase: React.FC<ShowcaseProps> = ({ durationMs = 8000, className }) => {
  const [index, setIndex] = useState(0);
  // Cuenta cada entrada a una escena: reinicia la barra de progreso y el ticket.
  const [cycle, setCycle] = useState(0);

  const go = (i: number) => {
    setIndex(i);
    setCycle((c) => c + 1);
  };
  const next = () => go((index + 1) % SCENES.length);

  const sceneFor = (key: (typeof SCENES)[number]['key'], active: boolean) => {
    switch (key) {
      case 'vapor': return <VaporScene active={active} />;
      case 'comanda': return <KdsScene active={active} />;
      case 'mesas': return <FloorScene active={active} />;
      default: return <ReceiptScene active={active} playKey={active ? cycle : -1} />;
    }
  };

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      data-tone={SCENES[index].tone}
      style={{ '--sc-dur': `${durationMs}ms` } as React.CSSProperties}
    >
      <div className={styles.dots} role="group" aria-label="Escenas">
        {SCENES.map((s, i) => (
          <button
            key={s.key}
            type="button"
            className={[styles.dot, i < index && styles.done, i === index && styles.on].filter(Boolean).join(' ')}
            aria-label={`Escena ${i + 1}: ${s.label}`}
            aria-current={i === index}
            onClick={() => go(i)}
          >
            <span className={styles.dotTrack}>
              {/* Al terminar de llenarse la barra pasa a la siguiente escena; con
                  "reducir movimiento" no hay animación y se queda en la actual. */}
              <i key={i === index ? cycle : 'idle'} onAnimationEnd={i === index ? next : undefined} />
            </span>
          </button>
        ))}
      </div>
      {SCENES.map((s, i) => (
        <div key={s.key} className={[styles.slide, i === index && styles.slideOn].filter(Boolean).join(' ')} aria-hidden={i !== index}>
          {sceneFor(s.key, i === index)}
        </div>
      ))}
    </div>
  );
};

export default Showcase;
